<script setup lang="ts">
import {
  SkyButton,
  SkyIcon,
  SkyLink,
  SkyScreenReader,
  useSkyBreakpoint,
} from '@groveco/skylight'
import type { MenuItem } from '@groveco/http-services'
import { ROUTES } from '~/constants/routes'

const isAccountDropdownActive = ref(false)
const { id: customerId, firstName, isLoggedIn, isVip } = useCustomer()
const { isLargeUp } = useSkyBreakpoint()
const { isExpanded } = useSearchSuggestions()

// itemCount will come from useCart once it is ported
const itemCount = ref(0)

const COUNT_MAX = 99
const itemCountUpdated = ref(false)
const showNavDrawer = ref(false)

const accountDropdownItems: MenuItem[] = [
  {
    id: 'account-refer',
    children: [],
    href: '/refer/',
    text: 'Refer Friends, Get $10',
  },
  {
    id: 'account-settings',
    children: [],
    href: ROUTES.accountSettings.to,
    text: 'Your Settings',
  },
  {
    id: 'account-cart',
    children: [],
    href: ROUTES.cartRoute.to,
    text: 'Your Cart',
  },
  {
    id: 'account-favorites',
    children: [],
    href: '/saved-products/',
    text: 'Favorites',
  },
  {
    id: 'account-subscriptions',
    children: [],
    href: ROUTES.subscriptions.to,
    text: 'Product Subscriptions',
  },
  {
    id: 'account-order-history',
    children: [],
    href: '/order-history/',
    text: 'Order History',
  },
  {
    id: 'account-review',
    children: [],
    href: '/review/',
    text: 'Review Products',
  },
  {
    id: 'account-logout',
    children: [],
    href: ROUTES.logoutRoute.to,
    text: ROUTES.logoutRoute.text,
  },
]

const aboutDropdownItems = computed<MenuItem[]>(() => {
  const items = [
    {
      id: 'about-story',
      children: [],
      href: ROUTES.about.to,
      text: ROUTES.about.text,
    },
    {
      id: 'about-grove-standard',
      children: [],
      href: ROUTES.groveStandard.to,
      text: ROUTES.groveStandard.text,
    },
  ]

  if (isLoggedIn.value) {
    return [
      {
        id: 'hiw-grove-works',
        children: [],
        href: ROUTES.howGroveWorks.to,
        text: ROUTES.howGroveWorks.text,
      },
      {
        id: 'about-subscribeAndSave',
        text: 'Subscribe and Save',
        href: '/learn-more-subscribe-and-save/',
        children: [],
      },
      ...items,
    ]
  }

  return [
    ...items,
    {
      id: 'about-wellness',
      children: [],
      href: ROUTES.wellnessRoute.to,
      text: ROUTES.wellnessRoute.text,
    },
  ]
})

const hiwDropdownItems: MenuItem[] = [
  {
    id: 'hiw-grove-works',
    children: [],
    href: ROUTES.howGroveWorks.to,
    text: ROUTES.howGroveWorks.text,
  },
  {
    id: 'about-subscribeAndSave',
    children: [],
    href: '/learn-more-subscribe-and-save/',
    text: 'Subscribe and Save',
  },
  {
    id: 'hiw-faq',
    children: [],
    href: ROUTES.frequentlyAsked.to,
    text: ROUTES.frequentlyAsked.text,
  },
  {
    id: 'hiw-contact',
    children: [],
    href: ROUTES.contactUs.to,
    text: ROUTES.contactUs.text,
  },
]

const showAccountDetails = computed<Boolean>(() =>
  Boolean(customerId.value && isLargeUp.value)
)

const displayCount = computed<string>(() => {
  return itemCount.value > COUNT_MAX ? `${COUNT_MAX}+` : `${itemCount.value}`
})

const displayName = computed<string>(() => {
  if (isLoggedIn.value && firstName.value) {
    return firstName.value
  }
  return 'Friend'
})

const showSearch = computed<Boolean>(() => !isLargeUp.value)

const isSearchFullWidth = computed<Boolean>(
  () => isExpanded.value && showSearch.value
)

const showHeaderLogo = computed<Boolean>(
  () => !isLoggedIn.value || isLargeUp.value
)

const showCondensedMobileHeader = computed<Boolean>(
  () => showSearch.value && showHeaderLogo.value
)

const showVipDot = computed(() => {
  // Restore once useVipGiftPicker is ported
  // if (isVip.value) {
  //   const { hasNotification } = useVipGiftPicker()
  //   return hasNotification.value
  // }
  return false
})

/**
 * Animate the cart badge when the item count increases.
 */
let countTimeout: ReturnType<typeof setTimeout> | undefined
watch(itemCount, (currentCount, lastCount) => {
  if (lastCount > currentCount) {
    return
  }

  itemCountUpdated.value = true
  if (countTimeout) {
    clearTimeout(countTimeout)
  }
  countTimeout = setTimeout(() => {
    itemCountUpdated.value = false
  }, 2000)
})
</script>

<template>
  <div class="TheHeaderNavigation">
    <div
      class="TheHeaderNavigation_Container"
      :class="{
        'TheHeaderNavigation_Container--condensed': showCondensedMobileHeader,
        'TheHeaderNavigation_Container--search': isSearchFullWidth,
      }"
    >
      <template v-if="!isSearchFullWidth">
        <nav v-if="isLargeUp" aria-label="Header menu" data-test-id="nav">
          <ul class="TheHeaderNavigation_ListGroup">
            <template v-if="isLoggedIn">
              <li>
                <SkyLink
                  class="TheHeaderNavigation_Link"
                  :to="ROUTES.memberHome.to"
                >
                  Your Grove
                </SkyLink>
              </li>
              <li>
                <SkyLink
                  class="TheHeaderNavigation_Link"
                  to="/catalog/?category=grove-collaborative"
                >
                  Shop Grove Co.
                </SkyLink>
              </li>
            </template>
            <li>
              <HeaderDropdown title="About" :items="aboutDropdownItems" />
            </li>
            <li v-if="!isLoggedIn">
              <HeaderDropdown title="How It Works" :items="hiwDropdownItems" />
            </li>
          </ul>
        </nav>
        <SkyButton
          v-else
          class="TheHeaderNavigation_MenuButton"
          data-test-id="menu"
          icon
          @click="showNavDrawer = true"
        >
          <SkyIcon name="menu" size="32" />
        </SkyButton>
        <TheHeaderLogo v-if="showHeaderLogo" class="TheHeaderNavigation_Logo" />
      </template>
      <SearchBarAutocomplete
        v-if="showSearch"
        class="TheHeaderNavigation_Search"
        show-condense-mobile-header
        data-test-id="header-nav-search-form"
      />

      <SkyButton
        v-if="isSearchFullWidth"
        class="TheHeaderNavigation_SearchCloseIcon"
        icon
        @click="isExpanded = false"
      >
        <SkyIcon name="close" size="24px" />
      </SkyButton>
      <nav v-else aria-label="Account settings">
        <ul
          class="TheHeaderNavigation_ListGroup TheHeaderNavigation_ListGroup-right"
        >
          <li v-if="isLoggedIn && isLargeUp">
            <SkyLink
              :to="ROUTES.vipHub.to"
              class="TheHeaderNavigation_Link"
              data-test-id="vip-hub"
            >
              <svg
                role="img"
                class="TheHeaderNavigation_LinkIcon"
                xmlns="http://www.w3.org/2000/svg"
                width="31"
                height="24"
              >
                <path
                  d="M16.428 9v7h-1.614V9h1.614ZM8.793 16h-.729L5 9h1.776l1.65 3.9 1.65-3.9h1.78l-3.063 7Z"
                />
                <path
                  fill-rule="evenodd"
                  d="M22.082 16v-2.558h.505c.796 0 1.421-.19 1.883-.571.463-.386.697-.933.697-1.65 0-.718-.234-1.264-.697-1.645C24.008 9.19 23.383 9 22.587 9h-2.126v7h1.62Zm0-5.58h.505c.663 0 .997.269.997.801s-.334.8-.997.8h-.505v-1.6Z"
                  clip-rule="evenodd"
                />
                <path
                  fill-rule="evenodd"
                  d="M0 12.118A9.118 9.118 0 0 1 9.118 3h12.764a9.118 9.118 0 0 1 0 18.235H9.118A9.118 9.118 0 0 1 0 12.118ZM9.118 5h12.764a7.118 7.118 0 0 1 0 14.235H9.118A7.118 7.118 0 0 1 9.118 5Z"
                  clip-rule="evenodd"
                />
              </svg>
              <div class="TheHeaderNavigation_VIPHub">
                {{ !isVip ? 'Join VIP' : 'VIP Hub' }}
              </div>
              <span
                v-if="showVipDot"
                class="TheHeaderNavigation_LinkBadge TheHeaderNavigation_LinkBadge-vip"
                data-test-id="vip-dot"
              >
                <SkyScreenReader>You have a new gift to choose</SkyScreenReader>
              </span>
            </SkyLink>
          </li>
          <li v-if="showAccountDetails" class="TheHeaderNavigation_Account">
            <SkyLink
              :to="
                isLoggedIn ? ROUTES.accountSettings.to : ROUTES.loginRoute.to
              "
              class="TheHeaderNavigation_AccountLink"
              data-test-id="account-link"
              @mouseover="isAccountDropdownActive = true"
              @mouseout="isAccountDropdownActive = false"
            >
              <svg
                role="presentation"
                class="TheHeaderNavigation_AccountIcon"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
              >
                <path
                  fill-rule="evenodd"
                  d="M2.251 17.1A10.952 10.952 0 0 1 1 12C1 5.925 5.925 1 12 1s11 4.925 11 11-4.925 11-11 11a10.98 10.98 0 0 1-8.656-4.21c0-.001 0 0 0 0a11.013 11.013 0 0 1-1.093-1.69Zm16.777.522A8.983 8.983 0 0 1 12 21a8.983 8.983 0 0 1-7.028-3.377 13.319 13.319 0 0 1 4.53-1.658 1 1 0 0 0 .533-1.695c-.69-.685-1.159-1.74-1.159-2.957 0-2.256 1.538-3.813 3.125-3.813s3.125 1.557 3.125 3.813c0 1.217-.468 2.272-1.158 2.957a1 1 0 0 0 .532 1.695 13.32 13.32 0 0 1 4.529 1.657Zm1.068-1.685a15.136 15.136 0 0 0-3.718-1.595c.478-.89.748-1.93.748-3.03 0-3.06-2.155-5.812-5.125-5.812s-5.125 2.753-5.125 5.813c0 1.1.27 2.14.749 3.03a15.133 15.133 0 0 0-3.72 1.594 9 9 0 1 1 16.191 0Z"
                  clip-rule="evenodd"
                />
              </svg>
              <div
                class="TheHeaderNavigation_AccountName"
                data-test-id="greeting"
              >
                Hello, {{ displayName }}
              </div>
              <span>{{ isLoggedIn ? 'Your Account' : 'Log In' }}</span>
            </SkyLink>

            <HeaderDropdown
              v-if="isLoggedIn"
              class="TheHeaderNavigation_AccountDropdown"
              data-test-id="account-dropdown"
              title=""
              label="Account"
              :items="accountDropdownItems"
              :active="isAccountDropdownActive"
            >
              <template #title>
                <span class="ScreenReader-only">Open account menu</span>
              </template>
            </HeaderDropdown>
          </li>
          <li>
            <SkyLink
              class="TheHeaderNavigation_Link"
              data-test-id="cart-link"
              :to="ROUTES.cartRoute.to"
            >
              <svg
                role="img"
                :width="isLargeUp ? '24' : '32'"
                :height="isLargeUp ? '24' : '32'"
                viewBox="0 0 24 24"
                fill="none"
                class="TheHeaderNavigation_LinkIcon TheHeaderNavigation_LinkIcon-cart"
                aria-labelledby="CartIcon-title"
                data-test-id="cart-icon"
              >
                <title id="CartIcon-title">Cart Icon</title>
                <path
                  fill-rule="evenodd"
                  stroke-width=".6"
                  d="M10.475 17.515a2.23 2.23 0 0 1 2.218 2.218 2.23 2.23 0 0 1-2.218 2.219 2.23 2.23 0 0 1-2.219-2.219 2.23 2.23 0 0 1 2.219-2.218Zm7.338 0c1.216 0 2.198 1.002 2.219 2.218a2.23 2.23 0 0 1-2.219 2.219 2.23 2.23 0 0 1-2.218-2.219 2.23 2.23 0 0 1 2.218-2.218Zm-7.338 1.344a.865.865 0 0 0-.875.874c0 .491.384.875.875.875.469 0 .874-.405.874-.875a.865.865 0 0 0-.874-.874Zm7.338 0a.865.865 0 0 0-.874.874c0 .491.384.875.874.875.47 0 .875-.405.875-.875a.865.865 0 0 0-.875-.874ZM4.052 2.88c1.023 0 1.906.64 2.162 1.557l.441 1.579h14.157c.674 0 1.325.299 1.744.79.441.49.581 1.13.418 1.727l-1.743 6.571c-.233.917-1.14 1.557-2.162 1.557H9.794c-1.023 0-1.907-.64-2.162-1.557L4.796 4.736a.76.76 0 0 0-.744-.533H1.68c-.396 0-.721-.299-.721-.662 0-.362.325-.661.72-.661h2.372Zm16.76 4.48H7.027l2.023 7.445a.76.76 0 0 0 .744.534h9.275a.76.76 0 0 0 .744-.534l1.743-6.57c.07-.214 0-.427-.14-.598a.796.796 0 0 0-.604-.277Z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                v-if="itemCount > 0"
                class="TheHeaderNavigation_LinkBadge TheHeaderNavigation_LinkBadge-cart"
                :class="{
                  'TheHeaderNavigation_LinkBadge-updated': itemCountUpdated,
                }"
                data-test-id="cart-badge"
                title="Total number of items in your cart"
              >
                {{ displayCount }}
              </span>
              <span v-if="isLargeUp">Cart</span>
            </SkyLink>
          </li>
        </ul>
      </nav>
    </div>
  </div>
  <transition name="TheHeaderNavigation_SlideIn">
    <TheNavDrawer
      v-if="showNavDrawer && !isLargeUp"
      @hide="showNavDrawer = false"
    />
  </transition>
</template>

<style lang="scss">
.TheHeaderNavigation {
  @include container-small;
  --container-margin: var(--spacing-3x);

  @include for-medium-up {
    --container-margin: var(--spacing-3x);
    --container-max: #{$container-large};
  }

  @include for-large-up {
    --container-margin: var(--spacing-8x);
  }

  &_Container {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    margin: var(--spacing-3x) 0;
    gap: var(--spacing-4x);

    &--condensed {
      grid-template-columns: auto auto 1fr auto;
    }

    &--search {
      grid-template-columns: 1fr auto;
    }

    @include for-large-up {
      margin: var(--spacing-4x) 0;
      grid-template-columns: 1fr auto 1fr;
    }
  }

  &_Logo {
    display: flex;
    justify-content: center;
  }

  &_MenuButton {
    color: var(--text-color-primary-ondark);
    max-height: var(--spacing-8x);

    // For pre-hydration
    @include for-large-up {
      opacity: 0;
    }
  }

  &_Cart {
    display: flex;
    margin-right: calc(var(--spacing-1x) * -1);
    @include for-medium-up {
      margin-right: 0;
    }
    font-size: var(--font-size-100);
  }

  &_ListGroup {
    margin: 0;
    padding: 0;
    display: flex;
    list-style-type: none;
    align-items: center;
    gap: var(--spacing-6x);

    &-right {
      justify-content: right;
    }
  }

  &_Link {
    display: flex;
    align-items: center;
    position: relative;
    --text-color-action-primary: var(--text-color-primary-ondark);
    --text-color-action-primary-hover: var(--text-color-action-primary);

    &:hover {
      border-color: transparent;
    }
  }

  &_LinkBadge {
    background: var(--color-teal);
    border-radius: var(--border-radius-pill);
    box-shadow: 0 0 0 1px var(--color-frost);
    color: var(--text-color-primary-ondark);
    display: block;
    position: absolute;
    aspect-ratio: 1;
    overflow: hidden;
    transition: 0.2s ease-in-out;

    &-cart {
      top: calc(var(--spacing-2x) * -1);
      right: 0;

      width: calc(var(--spacing-4x) + var(--spacing-halfx));
      height: calc(var(--spacing-4x) + var(--spacing-halfx));

      font-size: var(--font-size-50);
      line-height: 20px;
      text-align: center;

      @include for-large-up {
        left: var(--spacing-3x);
      }
    }

    &-vip {
      top: calc(var(--spacing-1x) * -1);
      right: calc(var(--spacing-1x) * -1);

      width: var(--spacing-2x);
      height: var(--spacing-2x);
    }

    &-updated {
      background-color: var(--surface-color-savings);
      transform: scale(1.21);
    }
  }

  &_LinkIcon {
    fill: currentColor;
    margin-right: var(--spacing-2x);

    &-cart {
      stroke: currentColor;
    }
  }

  &_Account {
    display: flex;
  }

  &_AccountDropdown {
    display: flex;

    .HeaderDropdown_Title {
      place-self: end;
      padding: 0;
    }

    .HeaderDropdown_ItemContainer {
      left: -132px;
    }
  }

  &_AccountLink {
    display: grid;
    grid-template-areas:
      'icon greeting'
      'icon account';
    grid-gap: 0px var(--spacing-3x);
    line-height: 1;
    text-wrap: nowrap;
    --text-color-action-primary: var(--text-color-primary-ondark);
    --text-color-action-primary-hover: var(--text-color-action-primary);

    &:hover,
    &:focus {
      border-color: transparent;
      text-decoration: underline;
    }
  }

  &_AccountIcon {
    fill: currentColor;
    grid-area: icon;
  }

  &_AccountName {
    font-size: var(--font-size-50);
  }

  &_Search {
    @include for-large-up {
      display: none;
    }
  }

  &_SearchCloseIcon {
    display: flex;
    color: var(--text-color-primary-ondark);
  }

  &_SlideIn {
    &-enter-active,
    &-leave-active {
      transition: transform 0.3s;
    }

    &-enter-from,
    &-leave-to {
      transform: translateX(-100%);
    }
  }
}
</style>
